import React from 'react'
import { Empty, Flex, List, Space } from 'antd'
import Avatar from '../../../../../../../../../components/common/avatar/Avatar'
import { getColorByIndex } from '../../../../../../../../../utils/colors.helper'
import Text from 'antd/es/typography/Text'
import { sqlToJsDate, sqlToTimeFromNow } from '../../../../../../../../../utils/date.helper'
import PatternIcon from '../../../../../../../../../components/common/PatternIcon'
import { ArrowRightOutlined } from '@ant-design/icons'
import {
    useFetchAnswerCategoryHistoryQuery,
    useFetchTextSubmissionDetailsQuery,
} from '../../../../../../../../../store/apis/submission.api'
import { primaryColor } from '../../../../../../../../../constants/styles'
import { isNumber } from 'lodash'
import {
    ActorTypes,
    getActorIcon,
    getActorName,
    NonHumanActors,
} from '../../../../../../../../../constants/actor-types'
import submissionSelectors from '../../../../../../../../../store/selectors/submission.selectors'
import { useSelector } from 'react-redux'
import { fetchTextSubmissionDetailsSelectors } from '../../../../../../../../../store/apis/utils/selectors/submission.api.selectors'
import { getConfidenceLevelNoun } from '../../../../../../../../../constants/confidence-levels'
import SubmissionApproveStatuses from '../../../../../../../../../constants/submission-approve-statuses'

const History = ({ answer, question }) => {
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)

    const { data: reviews, isFetching } = useFetchAnswerCategoryHistoryQuery({ id: answer.id })

    const { data: latestReview } = useFetchTextSubmissionDetailsQuery(
        { submissionId: openedSubmissionId },
        {
            selectFromResult: result =>
                fetchTextSubmissionDetailsSelectors.answerByQuestionId({ questionId: question.id }, result),
        },
    )

    // Create a map of deleted category names from the history records
    const deletedCategoryNames = {}
    if (reviews) {
        reviews.forEach(review => {
            if (review.deleted && review.patternName && review.categoryId) {
                deletedCategoryNames[review.categoryId] = review.patternName
            }
        })
    }

    // Combine and process all reviews
    let allReviews = []
    if (latestReview && isNumber(latestReview.categoryId)) allReviews.push(latestReview)
    if (reviews) allReviews.push(...reviews)
    allReviews = groupByTrainingRunId(allReviews)

    return (
        <List
            dataSource={allReviews}
            loading={{ spinning: isFetching, tip: 'Loading history...' }}
            locale={{ emptyText: <Empty description='The history is empty' /> }}
            renderItem={(review, index) => <HistoryItem 
                review={review} 
                previousReview={allReviews[index + 1]} 
                question={question}
                index={index}
                deletedCategoryNames={deletedCategoryNames}
            />}
        />
    )
}

const HistoryItem = ({ review, previousReview, question, deletedCategoryNames }) => {
    const isDeleted = review.deleted
    const newCategory = question?.categories?.find(category => category.id === review.categoryId)
    const oldCategoryId = previousReview?.categoryId
    const oldCategory = question?.categories?.find(category => category.id === oldCategoryId)
    const categoryChanged = oldCategoryId !== review.categoryId
    
    // Get the appropriate action description
    const userActionDescription = getUserActionDescription(review, categoryChanged, oldCategory, isDeleted)
    
    // Get actor details
    const actorName = getActorName(review.actor)
    const actorIcon = getActorIcon(review.actor)
    const actorColor = isNumber(review.actor?.id) ? getColorByIndex(review.actor?.id).value : primaryColor

    return (
        <List.Item key={review.createdAt}>
            <Flex vertical gap={8}>
                {/* Actor and action information */}
                <div>
                    <Avatar
                        icon={actorIcon}
                        size='small'
                        name={actorName}
                        color={actorColor}
                        className='mr-8'
                    />
                    <Text strong>{actorName}</Text> {userActionDescription}
                    <Text type='secondary' title={sqlToJsDate(review.createdAt)}>
                        {' '}{sqlToTimeFromNow(review.createdAt)}
                    </Text>
                </div>

                {/* Category change information */}
                {(categoryChanged || isDeleted) && (
                    <div className='ml-4'>
                        <Space size={4}>
                            {renderCategoryChange(isDeleted, categoryChanged, oldCategory, oldCategoryId, newCategory, review, deletedCategoryNames)}
                        </Space>
                    </div>
                )}
            </Flex>
        </List.Item>
    )
}

// Helper function to determine the user action description
const getUserActionDescription = (review, categoryChanged, oldCategory, isDeleted) => {
    if (isDeleted) {
        return 'deleted the category'
    }
    
    if (categoryChanged && oldCategory) {
        return 'changed the category'
    }
    
    if (NonHumanActors.includes(review.actor)) {
        return isNumber(review.confidence) 
            ? `classified with ${getConfidenceLevelNoun(review.confidence).toLowerCase()}`
            : 'classified'
    }
    
    if (review.approveStatus === SubmissionApproveStatuses.AUTO_APPROVED) {
        return 'auto-approved'
    }
    
    if (categoryChanged) {
        return isNumber(review.confidence) 
            ? `set the category with ${getConfidenceLevelNoun(review.confidence).toLowerCase()}`
            : 'set the category'
    }
    
    return isNumber(review.confidence) 
        ? `approved with ${getConfidenceLevelNoun(review.confidence).toLowerCase()}`
        : 'approved'
}

// Helper function to render the category change visualization
const renderCategoryChange = (isDeleted, categoryChanged, oldCategory, oldCategoryId, newCategory, review, deletedCategoryNames) => {
    // Show old category → new category for changes
    if (categoryChanged && !isDeleted && oldCategory) {
        return (
            <>
                <PatternIcon
                    id={oldCategoryId}
                    colorId={oldCategory.colorId}
                    showId={false}
                />
                {oldCategory?.name || 'None'} <ArrowRightOutlined />
                {renderCategoryName(newCategory, review, deletedCategoryNames)}
            </>
        )
    }

    // Only show the category for deletions or simple settings
    return renderCategoryName(newCategory, review, deletedCategoryNames, isDeleted)
}

// Helper function to render a category name with appropriate styling
const renderCategoryName = (newCategory, review, deletedCategoryNames, isDeleted = false) => {
    if (isDeleted) {
        return (
            <>
                <PatternIcon 
                    id={review.categoryId} 
                    colorId={review.categoryId} 
                    showId={false}
                />
                <Text delete type="danger">
                    {review.patternName || deletedCategoryNames[review.categoryId] || 'Unknown category'}
                </Text>
            </>
        )
    }

    if (newCategory) {
        return (
            <>
                <PatternIcon 
                    id={newCategory.id} 
                    colorId={newCategory.colorId} 
                    showId={false}
                />
                {newCategory.name}
            </>
        )
    }

    return (
        <>
            <PatternIcon 
                id={review.categoryId} 
                colorId={review.categoryId} 
                showId={false}
            />
            {deletedCategoryNames[review.categoryId] || 'Unknown category'}
        </>
    )
}

// Group reviews by training run to consolidate AI assistant actions
const groupByTrainingRunId = reviews => {
    const groupedReviews = []
    const seenTrainingRunIds = new Set()

    reviews.forEach(review => {
        if (isNumber(review.trainingRunId)) {
            if (!seenTrainingRunIds.has(review.trainingRunId)) {
                const groupedReview = {
                    ...review,
                    actor: ActorTypes.SENSE_ASSISTANT,
                }
                groupedReviews.push(groupedReview)
                seenTrainingRunIds.add(review.trainingRunId)
            }
        } else {
            groupedReviews.push(review)
        }
    })

    return groupedReviews
}

export default History
